import gsap from 'gsap';
import { AmbientLight, Color, DirectionalLight, Group, PointLight, Vector3, Matrix4, Object3D } from 'three';
// Rotació Y del character per scroll (Products). Signat: negatiu = sentit contrari als thumbnails.
// productsBaseDeg = rotació base (graus) a ProductsScene: el character mira a -30° respecte el
// centre (posició base de HomeScene). La rotació per scroll se suma a aquesta base.
// productsBaseDelay = retard (s) abans d'animar la rotació base en entrar a Products: un punt
// intermig entre el show de la pàgina i el show dels thumbnails (entreté l'usuari mentrestant).
export const CHAR_ROT_SETTINGS = { speed: 0.002, productsBaseDeg: -30, productsBaseDelay: 0.3 };

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Sizes } from '../_app/cuchillo/core/Sizes';
import { Maths } from '../_app/cuchillo/utils/Maths';
import Character from './Character';
import CharacterAnimationController from './CharacterAnimationController';
import BGSphere from './BGSphere';
export { BG_SETTINGS } from './BGSphere';

// ─── Camera intro settings (animació inicial de la Home) ──────────────────────
// La càmera surt d'aquesta posició i s'anima fins a la posició de repòs (cameraPos).
// Intro del character: translació de POSICIÓ (no moviment de càmera). El character ve
// d'un offset (Y amunt + Z enrere = lluny) i anima fins a 0. startYFactor × ALÇADA DEL CHARACTER;
// startZ és l'offset Z en món (negatiu = lluny de càmera → es veu petit i creix per perspectiva).
export const CHAR_INTRO_SETTINGS = {
  startYFactor: 0.714, // Y inicial (× charHeight) → 0 (= 0.5 × 800/560, preserva 1440×800)
  startZ: -3000,      // Z inicial (offset, negatiu = lluny) → 0 (zoom-in per perspectiva)
  duration: 3,
};

// ─── Animació general (cinemàtica del character en 3 passos) ───────────────────
// Tots els timings/factors són tunejables al debug pane.
// Cinemàtica del character en 3 passos (translació pura del grup + offset Y del helmet).
// Els factors Y van × ALÇADA DEL CHARACTER (_charHeight, el seu rect DOM en VH), NO × viewport:
// el character es dimensiona en VH (#characterTarget) i canvia de fracció per breakpoint (70vh→42vh),
// així que lligar-ho a la seva pròpia alçada dóna LA MATEIXA animació a qualsevol mida de pantalla.
// (Z en unitats de món.) Valors back-solved perquè a 1440×800 [charH=0.70·800=560] siguin idèntics.
export const GENERAL_SETTINGS = {
  // Estat inicial del helmet: offset Y respecte el character (× charHeight). Aplicat a TOTS els inits/resets.
  helmetRestYFactor: -1.143,  // (= -0.8 × 800/560, preserva 1440×800)

  // ── PAS 1 ── show helmet snap + puja'l; mou el grup (Y centra helmet + apropa Z); activa anim tapon
  helmetShowDuration: 3,     // durada de la pujada del helmet (rest → helmetToYFactor)
  helmetToYFactor: 0,        // helmet: Y final de la pujada (× charHeight) — sobre el cap
  step1Duration: 2,          // durada del moviment del grup (Y + Z)
  // Pujada Y del grup = factor × charHeight, amb factor per breakpoint (vegeu _step1GroupYFactor):
  step1GroupYFactor: 1.0,        // DESKTOP (> $tabletLandscape 1024px) — preserva 1440×800
  step1GroupYFactorTablet: .85, // TABLET (≤ $tabletLandscape 1024px, > $smartphone 480px)
  step1GroupYFactorMobile: 0.7,  // MÒBIL (≤ $smartphone 480px)
  step1CharZ: 450,           // grup: apropament Z a la càmera (offset món, + = més a prop)
  taponAnimDelay: 1.1,       // delay (s) per activar l'animació tapon, respecte l'inici del Pas 1

  // ── PAS 2 ── el character torna a la posició inicial (charRig → 0)
  step2Delay: 3,             // delay (s) per arrencar el Pas 2, respecte l'inici del Pas 1
  // changePage: delay (s) DESPRÉS d'arrencar el Pas 2 abans de canviar de pàgina. 0 = a l'inici
  // del Pas 2; ~3.5 = a l'onComplete original (final del Pas 2). 1.75 = a mig camí (evita el glitch).
  changePageDelay: 1.75,
  step2YDuration: 4,         // durada del moviment Y (ease out)
  step2ZDuration: 3.5,       // durada del moviment Z (ease out, diferent de Y)
  step2ZStartFactor: 0.04,   // la Z comença a aquest factor de la durada de l'animació tapon
};

// 4 grups, cadascun el seu render pass (vegeu WebGLSketch.renderScene):
// Layer 0: character + thumbnails       → Pass 1 (il·luminat per les char lights, layer 3)
// Layer 3: llums del character (recollides al Pass 1) — no afecten els productes
// Layer 4: __box, __bottle, tapon luminaris → Pass 2 (NOMÉS llums luminaris + ambient)
// Layer 5: __box, __bottle, tapon obscurios → Pass 3 (NOMÉS llums obscurios + ambient)
// Layer 2: __obj                        → Pass 4 (SENSE llums; renderitzat ÚLTIM)
export const OBJ_LAYER = 2;         // __obj (sense llums, render pass propi al final)
export const PROD_LAYER = 4;        // luminaris (llums de producte commutables per pàgina)
export const OBSCURIOS_LAYER = 5;   // obscurios (llums pròpies, sempre presents)

export const CHAR_LAYER = 3;   // llums pròpies del character (Pass 1, no afecten productes)
export const CHAR_LIGHTS = {
  dir1: { color: '#8e8e8e', intensity: 9.1, posX: 0.7, posY: 0.5, posZ: 0.7 },
  dir2: { color: '#8e8e8e', intensity: 1.5, posX: -0.4, posY: 0.4, posZ: 0.2 },
  point: { color: '#000000', intensity: 0.543, posX: -0.2, posY: -0.2, posZ: 0.13, distance: 8000, decay: 1.03 },
};

// ─── Tapon colors ─────────────────────────────────────────────────────────────
export const TAPON_SCROLL_SETTINGS = {
  luminarColor: '#F1F1F1',
  obscurColor: '#000000',
};

// ─── Light settings ─────────────────────────────────────────────────────────
export const LIGHT_SETTINGS = {
  ambient: { intensity: 1.5 },
};

// Llums de producte per família (cada preset = la família que il·lumina; layer a _initProductLights).
// luminaris = objectes de TEXTURA BLANCA → necessiten llum FOSCA (grisos + point negre, layer 4).
export const PRODUCT_LIGHTS_LUMINARIS = {
  dir1: { color: '#8e8e8e', intensity: 9.1, posX: 0.7, posY: 0.5, posZ: 0.7 },
  dir2: { color: '#8e8e8e', intensity: 1.5, posX: -0.4, posY: 0.4, posZ: 0.2 },
  point: { color: '#000000', intensity: 0.543, posX: -0.2, posY: -0.2, posZ: 0.13, distance: 8000, decay: 1.03 },
};

// obscurios = objectes FOSCOS → necessiten llum BLANCA (high-key, layer 5).
export const PRODUCT_LIGHTS_OBSCURIOS = {
  dir1: { color: '#ffffff', intensity: 6, posX: 0.7, posY: 0.5, posZ: 0.7 },
  dir2: { color: '#ffffff', intensity: 4, posX: -0.4, posY: 0.4, posZ: 0.2 },
  point: { color: '#ffffff', intensity: 0.25, posX: -0.2, posY: -0.2, posZ: 0.13, distance: 8000, decay: 1.03 },
};

// Posicions de les llums de producte en mode HOME (per família), ajustables al debug pane
// (folder Home lights). Factors: posX/posZ × WIDTH, posY × HEIGHT.
// - luminaris: mateixes posicions que el preset OG, amb lookAt al tapon first-child (esquerra).
// - obscurios: posicions OG amb la X invertida (mirall), amb lookAt al tapon second-child (dreta).
export const HOME_LIGHT_SETTINGS = {
  luminaris: {
    dir1: { posX: 0.04, posY: 0.09, posZ: 0.54, intensity: 7.4 },
    dir2: { posX: -0.4, posY: 0.4, posZ: 0.2, intensity: 1.5 },
    point: { posX: -0.2, posY: -0.2, posZ: 0.39, intensity: 0.5 }, // factor (× WIDTH)
  },
  obscurios: {
    dir1: { posX: -0.09, posY: 0, posZ: 0.22, intensity: 14.8 },
    dir2: { posX: 0.39, posY: -0.2, posZ: 2, intensity: 12.4 },
    point: { posX: 0.26, posY: -0.04, posZ: 0.29, intensity: 0.717 }, // factor (× WIDTH)
  },
};

// ─── Scene ───────────────────────────────────────────────────────────────────
export default class Scene {
  // group  → inside WebGLSketch.group (follows mouse rotation) — ambient light
  // charGroup → directly in WebGLSketch.scene (no mouse rotation) — character, lights
  static group = new Group();
  static charGroup = new Group();

  static _initialized = false;
  static _ambientLight = null;

  // Product lights (PROD_LAYER=4) — __box, __bottle, tapon.glb (commutables luminaris/obscurios)
  static _prodDir1 = null;
  static _prodDir2 = null;
  static _prodPoint = null;

  // Obscurios lights (OBSCURIOS_LAYER=5) — persistents, per als objectes obscurios (preset OBSCURIOS)
  static _obsDir1 = null;
  static _obsDir2 = null;
  static _obsPoint = null;

  // Char lights (CHAR_LAYER=3) — llums pròpies del character (recollides al Pass 1)
  static _charDir1 = null;
  static _charDir2 = null;
  static _charPoint = null;

  // Targets dels direccionals de producte (lookAt per família). Mode 'home' → cada
  // família apunta al seu tapon-single; mode 'default' → apunten a l'origen (OG).
  static _lumTarget = new Object3D();
  static _obsTarget = new Object3D();
  static _lightsMode = 'default'; // 'default' (lateral OG) | 'home' (frontal X=0 + lookAt família)

  // Character + tapon
  static character = null;
  static _charScrollZ = 0;
  static characterState = 'HOME';
  static characterVisible = false;
  static _productsReady = false; // true mentre el character està inicialitzat per Products (evita re-setup)
  static _charReady = false; // true quan el model del character ha acabat de carregar
  static _pendingProductsContainer = null; // container de Products en espera si el model encara carrega
  static _hideProxy = null;
  static _targetEls = {};
  static _scrollVel = 0;
  static _charBaseRotY = 0; // rotació base Y (rad) del character — 0 a Home, -30° a Products
  static _charRotDelayed = null; // delayedCall que dispara la rotació base de Products
  static _charOpacityProxy = null;
  static _bgVisible = false;

  // ─── Char rig: movem el character (no la càmera). La càmera queda estàtica i
  // apliquem al rig T = C_rest · C(t)⁻¹ perquè el character es vegi exactament com
  // si la càmera estigués a la posició virtual mirant l'origen. ───────────────────
  static charRig = new Group();
  static _restCamMatrix = null;
  static _virtualCam = new Object3D();
  static _tmpMat = new Matrix4();
  static _lastVCam = new Vector3();
  static _camProxy = null;
  static _generalTL = null;
  static _generalSeq = null; // delayedCall que encadena els 3 passos de la cinemàtica
  static _changePageDelayed = null; // delayedCall del changePage dins el Pas 2 (vegeu changePageDelay)
  static _taponAnimDelayed = null; // delayedCall que activa l'animació tapon dins el Pas 1

  // Color buffers per a crossfade de llums (evita allocations per frame)
  static _ca = new Color();
  static _cb = new Color();

  // ─── Init ─────────────────────────────────────────────────────────────────

  static init() {
    WebGLSketch.group.add(this.group);
    WebGLSketch.scene.add(this.charGroup);
    this.charGroup.add(this.charRig); // el character va dins del rig (les llums queden a charGroup)
    this.charGroup.add(this._lumTarget); // targets dels direccionals de producte (lookAt família)
    this.charGroup.add(this._obsTarget);
    this._lastVCam.copy(WebGLSketch.defaults.cameraPos);

    this._ambientLight = new AmbientLight(0xffffff, LIGHT_SETTINGS.ambient.intensity);
    this._ambientLight.layers.enable(PROD_LAYER);      // ambient a Pass B layer 4
    this._ambientLight.layers.enable(OBSCURIOS_LAYER); // ambient a Pass B layer 5
    this.group.add(this._ambientLight);

    // BGSphere.init() NO es crida aquí: és lazy (veure warmBGSphere/showBGSphere). Pàgines
    // que mai el mostren (legals, contact...) no paguen mai el cost de compilar-lo.

    this._initProductLights(); // llums de producte persistents (luminaris L4 + obscurios L5)
    this._initCharacter();
  }

  // Crea un set de 3 llums (dir1/dir2/point) amb un preset a un layer concret.
  static _makeLightSet(preset, layer) {
    const dir1 = new DirectionalLight(new Color(preset.dir1.color), preset.dir1.intensity);
    dir1.position.set(preset.dir1.posX * Metrics.WIDTH, preset.dir1.posY * Metrics.HEIGHT, preset.dir1.posZ * Metrics.WIDTH);
    dir1.layers.set(layer);
    this.charGroup.add(dir1);

    const dir2 = new DirectionalLight(new Color(preset.dir2.color), preset.dir2.intensity);
    dir2.position.set(preset.dir2.posX * Metrics.WIDTH, preset.dir2.posY * Metrics.HEIGHT, preset.dir2.posZ * Metrics.WIDTH);
    dir2.layers.set(layer);
    this.charGroup.add(dir2);

    const point = new PointLight(new Color(preset.point.color), preset.point.intensity * Metrics.WIDTH, preset.point.distance, preset.point.decay);
    point.position.set(preset.point.posX * Metrics.WIDTH, preset.point.posY * Metrics.HEIGHT, preset.point.posZ * Metrics.WIDTH);
    point.layers.set(layer);
    this.charGroup.add(point);

    return { dir1, dir2, point };
  }

  // Llums pròpies del character (CHAR_LAYER=3). Es recullen al Pass 1 (càmera amb layer 3
  // habilitat) i il·luminen el character sense afectar els productes.
  static _initCharLights() {
    const s = CHAR_LIGHTS;
    this._charDir1 = new DirectionalLight(new Color(s.dir1.color), s.dir1.intensity);
    this._charDir1.position.set(s.dir1.posX * Metrics.WIDTH, s.dir1.posY * Metrics.HEIGHT, s.dir1.posZ * Metrics.WIDTH);
    this._charDir1.layers.set(CHAR_LAYER);
    this.charGroup.add(this._charDir1);

    this._charDir2 = new DirectionalLight(new Color(s.dir2.color), s.dir2.intensity);
    this._charDir2.position.set(s.dir2.posX * Metrics.WIDTH, s.dir2.posY * Metrics.HEIGHT, s.dir2.posZ * Metrics.WIDTH);
    this._charDir2.layers.set(CHAR_LAYER);
    this.charGroup.add(this._charDir2);

    this._charPoint = new PointLight(new Color(s.point.color), s.point.intensity * Metrics.WIDTH, s.point.distance, s.point.decay);
    this._charPoint.position.set(s.point.posX * Metrics.WIDTH, s.point.posY * Metrics.HEIGHT, s.point.posZ * Metrics.WIDTH);
    this._charPoint.layers.set(CHAR_LAYER);
    this.charGroup.add(this._charPoint);
  }

  // Llums de producte PERSISTENTS (no es disposen mai): luminaris (layer 4) i obscurios (layer 5).
  // Cada objecte va al layer del seu tipus i sempre té les seves llums.
  static _initProductLights() {
    const lum = this._makeLightSet(PRODUCT_LIGHTS_LUMINARIS, PROD_LAYER);
    this._prodDir1 = lum.dir1; this._prodDir2 = lum.dir2; this._prodPoint = lum.point;

    const obs = this._makeLightSet(PRODUCT_LIGHTS_OBSCURIOS, OBSCURIOS_LAYER);
    this._obsDir1 = obs.dir1; this._obsDir2 = obs.dir2; this._obsPoint = obs.point;

    // lookAt per família: els direccionals de cada família comparteixen el seu target.
    this._prodDir1.target = this._lumTarget; this._prodDir2.target = this._lumTarget;
    this._obsDir1.target = this._obsTarget; this._obsDir2.target = this._obsTarget;
  }

  // ─── Toggle posició/intensitat llums de producte (Home frontal vs lateral OG) ──
  // Aplica posició + intensitat a les 6 llums. frontal=true → HOME_LIGHT_SETTINGS
  // (frontals, ajustables); frontal=false → presets OG (lateral esquerra).
  // Point lights: la intensitat és un factor (× WIDTH), com a _makeLightSet.
  static _applyProductLightPositions(frontal) {
    const place = (light, p, isPoint) => {
      if (!light) return;
      light.position.set(p.posX * Metrics.WIDTH, p.posY * Metrics.HEIGHT, p.posZ * Metrics.WIDTH);
      if (p.intensity != null) light.intensity = isPoint ? p.intensity * Metrics.WIDTH : p.intensity;
    };
    const lum = frontal ? HOME_LIGHT_SETTINGS.luminaris : PRODUCT_LIGHTS_LUMINARIS;
    const obs = frontal ? HOME_LIGHT_SETTINGS.obscurios : PRODUCT_LIGHTS_OBSCURIOS;
    place(this._prodDir1, lum.dir1, false); place(this._prodDir2, lum.dir2, false); place(this._prodPoint, lum.point, true);
    place(this._obsDir1, obs.dir1, false); place(this._obsDir2, obs.dir2, false); place(this._obsPoint, obs.point, true);
  }

  // Re-aplica les posicions HOME en viu (cridat des del debug pane en ajustar-les).
  static refreshHomeLights() {
    if (this._lightsMode === 'home') this._applyProductLightPositions(true);
  }

  // Mode HOME: llums frontals (HOME_LIGHT_SETTINGS). Els targets els alimenta HomeScene.loop (lookAt al tapon).
  static setLightsHome() {
    this._lightsMode = 'home';
    this._applyProductLightPositions(true);
  }

  // Mode DEFAULT (lateral OG): posicions dels presets + direccionals mirant l'origen.
  static setLightsDefault() {
    this._lightsMode = 'default';
    this._applyProductLightPositions(false);
    this._lumTarget.position.set(0, 0, 0);
    this._obsTarget.position.set(0, 0, 0);
  }

  // Apunta els direccionals de cada família al seu tapon (posicions en món).
  // Només té efecte en mode HOME. targets: { luminaris:Vector3, obscurios:Vector3 }
  static updateFamilyLightTargets(targets) {
    if (this._lightsMode !== 'home' || !targets) return;
    if (targets.luminaris) this._lumTarget.position.copy(targets.luminaris);
    if (targets.obscurios) this._obsTarget.position.copy(targets.obscurios);
  }

  static _initCharacter() {
    const targetEl = GetBy.id('characterTarget');
    if (!targetEl) return;

    this.character = new Character({
      dom: targetEl,
      model: targetEl.dataset.model,
      model2: targetEl.dataset.model2 || null,
      diffusion: targetEl.dataset.diffuse || '',
      hasFlotation: true,
      repulsion: true,
      cols: .5,
    });

    this.character.init().then(() => {
      // El character (cos) ES il·lumina amb les SEVES llums (CHAR_LAYER=3, recollides al
      // Pass 1). Es queda a layer 0 (Pass 1) i es veu amb el seu material + char lights +
      // ambient, sense afectar els productes (box/bottle/tapon, layers 4/5).
      this._initCharLights();

      // Tapon (helmet) meshes → PROD_LAYER únic (layers.set elimina layer 0 → Pass A no el renderitza)
      this.character._scene2?.traverse(obj => {
        if (obj.isMesh) obj.layers.set(PROD_LAYER);
      });
      // Assegura l'estat inicial: animació tapon i character a frame 0 + HIDE CABESA = 0
      CharacterAnimationController.setTaponToStart();
      this._setHelmetY(GENERAL_SETTINGS.helmetRestYFactor); // helmet a l'offset inicial (-20% Y)

      // Inicialitza character a opacity 0 i tapon a opacity 0
      this._charScrollZ = 0;
      this._setCharOpacity(0);
      this._setTaponOpacity(0);

      // Inicialitza el tapon amb el color del primer type del WidgetTapon
      const firstTypeEl = GetBy.selector('[data-class="WidgetTapon"] [data-type]')[0];
      if (firstTypeEl) {
        const t = firstTypeEl.dataset.type;
        this.setTaponTypesCrossFade(t, t, 0);
      }

      this._targetEls['HOME'] = targetEl;
      this.character.active = true;
      this.charRig.add(this.character); // dins del rig (no directament a charGroup)
      this.character.resize();

      // El character ja està carregat. Si havíem entrat directament a Products mentre el
      // model encara carregava, el setup de Products es va ajornar (vegeu
      // initCharacterForProducts): l'executem ara, DESPRÉS del _setCharOpacity(0) d'aquí,
      // perquè no quedi invisible (race: el .then() arribava després de l'afterShow).
      this._charReady = true;
      if (this._pendingProductsContainer) {
        const container = this._pendingProductsContainer;
        this._pendingProductsContainer = null;
        this.initCharacterForProducts(container);
      }
      // Home havia demanat el warm-up de GPU però el model encara carregava → fes-lo ara,
      // amb el character ja al scene (encara emmascarat pel preloader).
      if (this._wantPrewarm) { this._wantPrewarm = false; this._prewarm(); }
    });
  }

  // ─── GPU warm-up (només Home, mentre el preloader tapa la pantalla) ─────────
  // El render loop corre durant el preloader però els objectes que apareixen al reveal estan
  // a visible=false → no es renderitzen → la GPU no compila els seus shaders ni puja
  // geometria/textures fins al reveal (stall). Aquí forcem un render real d'aquests objectes
  // mentre el preloader els tapa, perquè al reveal ja estigui tot calent.
  static _wantPrewarm = false;
  static _prewarmed = false;

  static requestPrewarm() {
    if (this._prewarmed) return;
    if (this._charReady) this._prewarm();   // character ja carregat → escalfa ara
    else this._wantPrewarm = true;          // encara carrega → s'ajorna al seu .then()
  }

  static _prewarm() {
    const r = WebGLSketch.renderer;
    if (!r || this._prewarmed) return;
    this._prewarmed = true;

    // Fem visibles temporalment els meshes ocults (character/helmet/BGSphere…). L'opacity es
    // manté a 0 (no es veu res) però el render sí que fa la feina de GPU. Restaurem després.
    const hidden = [];
    WebGLSketch.scene.traverse(obj => {
      if (obj.isMesh && obj.visible === false) { hidden.push(obj); obj.visible = true; }
    });

    // Un render dels 4 passes → compila shaders (un programa per pass/família de llums) i
    // puja geometria + textures a GPU.
    WebGLSketch.renderScene();

    for (let i = 0; i < hidden.length; i++) hidden[i].visible = false;
  }

  // ─── Character visibility ─────────────────────────────────────────────────

  // depthWrite = value >= 1: quan és transparent (fade) NO escriu depth → no ocluou el que va
  // darrere (els thumbnails de Products, que es renderitzen en un pass posterior compartint
  // depth). A opacity 0 a més visible=false. A opacity 1 (opac) sí escriu depth (self-occlusion).
  //
  // keepDepth: força depthWrite=true també durant el fade. El character és un model amb
  // geometria que se solapa; amb alpha blend i SENSE escriure depth, els triangles del darrere
  // també es pinten (no s'ocluen) → "textura trencada"/semitransparent. Escrivint depth cada
  // fragment fa depth-test contra si mateix i el fade es veu net. S'usa a la intro de la Home,
  // on no hi ha res al darrere que hagi de transparentar-se (a diferència de Products).
  static _setCharOpacity(value, keepDepth = false) {
    if (!this.character?.object?.scene) return;
    this.character.object.scene.traverse(obj => {
      if (obj.isMesh) { obj.material.transparent = true; obj.material.opacity = value; obj.material.depthWrite = keepDepth || value >= 1; obj.visible = value > 0; }
    });
  }

  static _setTaponOpacity(value) {
    if (!this.character?._scene2) return;
    this.character._scene2.traverse(obj => {
      if (obj.isMesh) { obj.material.transparent = true; obj.material.opacity = value; obj.material.depthWrite = value >= 1; obj.visible = value > 0; }
    });
  }

  static _setCharacterOpacity(value) {
    this._setCharOpacity(value);
    this._setTaponOpacity(value);
  }

  // Fade out character i tapon + reset a l'estat inicial.
  static hideCharacter(duration = 1) {
    this._productsReady = false; // amagar el character → ja no està "ready" per Products

    // Si ja està marcat com a no-visible (p.ex. el fade de Products.beforeHide ja ha corregut
    // o s'ha interromput abans d'acabar), NO refem el fade (faria un flash 1→0), però forcem
    // l'estat amagat perquè el character/helmet no quedi visible a la pàgina destí.
    if (!this.characterVisible) {
      if (this._hideProxy) { gsap.killTweensOf(this._hideProxy); this._hideProxy = null; }
      this._setCharOpacity(0);
      this._setTaponOpacity(0);
      return;
    }
    if (this._charOpacityProxy) gsap.killTweensOf(this._charOpacityProxy);
    if (this._hideProxy) gsap.killTweensOf(this._hideProxy);
    this._resetCharRig(); // torna el character a la posició de repòs (rig identitat)
    this._hideProxy = { char: 1, tapon: 1 };

    // Character (Pass A)
    gsap.to(this._hideProxy, {
      char: 0, duration, ease: 'power2.out',
      onUpdate: () => { if (this._hideProxy) this._setCharOpacity(this._hideProxy.char); },
      onComplete: () => {
        CharacterAnimationController.stopBase();
        this._charScrollZ = 0;
        this._setCharOpacity(0);
        this._setTaponOpacity(0);
        this.characterVisible = false;
        this._hideProxy = null;
      }
    });

    // Tapon (helmet): MATEIX fade que el body (duration + ease idèntics) perquè
    // desapareguin exactament igual. (El truc del fade ràpid per la silueta ja no cal:
    // _setTaponOpacity posa visible=false a opacity 0.)
    gsap.to(this._hideProxy, {
      tapon: 0, duration, ease: 'power2.out',
      onUpdate: () => { if (this._hideProxy) this._setTaponOpacity(this._hideProxy.tapon); }
    });
  }

  // Inicialitza el character a l'estat inicial:
  // character opacity 0, z = -zDistance, tapon opacity 0
  static showCharacter() {
    this._charScrollZ = 0;
    this._setCharOpacity(0);
    this._setTaponOpacity(0);
    CharacterAnimationController.startBase(); // Home: reprèn _BASE
    this.characterVisible = true;
  }

  // Animació d'entrada de la Home:
  // - posició: translació Y del character (animateCharIntro), NO moviment de càmera
  // - opacity character: 0→1 (fade-in)
  // - tapon: opacity 0 (estat inicial; apareix amb l'animació general)
  static introCharacter() {
    // Reset de l'estat inicial: animacions a frame 0 + HIDE CABESA = 0 + tapon opacity 0
    CharacterAnimationController.setTaponToStart();
    CharacterAnimationController.startBase();
    this.resetTaponLights(); // reset de les llums del tapon.glb al tipus per defecte
    this._setHelmetY(GENERAL_SETTINGS.helmetRestYFactor); // helmet a l'estat inicial (-20% Y)

    this._charScrollZ = 0; // el character es queda a la posició de repòs
    this._setCharOpacity(0);
    this._setTaponOpacity(0);
    this.characterVisible = true;
    // Parallax del mouse actiu a Home, però desactivat FINS que la intro acabi
    // (Home crida Scene.enableCharacterMouse a l'onComplete de la intro).
    if (this.character) { this.character._hasRepulsion = true; this.character._mouseInputDisabled = true; }

    // Estat inicial: rig net + posició d'ORIGEN de la intro (lluny) + opacitat 0. La POSICIÓ
    // l'anirà movent el progrés de l'scroll (WidgetHomeSpacer → setCharacterIntroProgress); la
    // OPACITAT la fa showCharacter() (fade únic). Tots dos disparats des de la Home.
    this._resetCharRig();
    this.setCharacterIntroProgress(0);
    this._setCharOpacity(0);
  }

  // ─── Char rig helpers (càmera estàtica, movem el character) ─────────────────

  // Matriu de la càmera en repòs (a cameraPos, mirant l'origen). Es calcula un cop.
  static _ensureRestCamMatrix() {
    if (this._restCamMatrix) return;
    const rest = WebGLSketch.defaults.cameraPos;
    this._virtualCam.position.copy(rest);
    this._virtualCam.up.set(0, 1, 0);
    this._virtualCam.lookAt(0, 0, 0);
    this._virtualCam.updateMatrix();
    this._restCamMatrix = this._virtualCam.matrix.clone();
  }

  // Aplica al charRig la transformació T = C_rest · C(t)⁻¹, on C(t) és la càmera virtual
  // a (px,py,pz) mirant l'origen. Resultat: el character es veu EXACTAMENT com si la
  // càmera s'hagués mogut allà, però la càmera real es manté estàtica.
  static _applyCharRigVirtualCamera(px, py, pz) {
    this._ensureRestCamMatrix();
    this._lastVCam.set(px, py, pz);

    this._virtualCam.position.set(px, py, pz);
    this._virtualCam.up.set(0, 1, 0);
    this._virtualCam.lookAt(0, 0, 0);
    this._virtualCam.updateMatrix();

    this._tmpMat.copy(this._virtualCam.matrix).invert(); // C(t)⁻¹
    this._tmpMat.premultiply(this._restCamMatrix);        // C_rest · C(t)⁻¹
    this._tmpMat.decompose(this.charRig.position, this.charRig.quaternion, this.charRig.scale);
  }

  // Rig a identitat (character a la seva posició de repòs / DOM)
  static _resetCharRig() {
    if (this._camProxy) { gsap.killTweensOf(this._camProxy); this._camProxy = null; }
    if (this._generalTL) { this._generalTL.kill(); this._generalTL = null; }
    this.charRig.position.set(0, 0, 0);
    this.charRig.quaternion.identity();
    this.charRig.scale.set(1, 1, 1);
    this._lastVCam.copy(WebGLSketch.defaults.cameraPos);
  }

  // Suavitza l'arribada a Products: anima el rig de la pose cinemàtica → identitat
  // (en comptes del snap de _resetCharRig). El loop ja lerpa posició/escala en paral·lel.
  static _animateCharRigToRest(duration = 1.2, ease = 'power2.out') {
    if (this._generalTL) { this._generalTL.kill(); this._generalTL = null; }
    if (this._camProxy) gsap.killTweensOf(this._camProxy);

    const rest = WebGLSketch.defaults.cameraPos;

    // Si el rig ja és a repòs (cap cinemàtica prèvia), snap directe.
    if (this._lastVCam.distanceTo(rest) < 1) { this._resetCharRig(); return; }

    this._camProxy = { x: this._lastVCam.x, y: this._lastVCam.y, z: this._lastVCam.z };
    gsap.to(this._camProxy, {
      x: rest.x, y: rest.y, z: rest.z, duration, ease,
      onUpdate: () => this._applyCharRigVirtualCamera(this._camProxy.x, this._camProxy.y, this._camProxy.z),
      onComplete: () => {
        this.charRig.position.set(0, 0, 0);
        this.charRig.quaternion.identity();
        this.charRig.scale.set(1, 1, 1);
        this._camProxy = null;
        this._lastVCam.copy(rest);
      }
    });
  }

  // Intro del character CONNECTADA A L'SCROLL: p 0→1 mou el charRig en Y i Z del MATEIX origen
  // que la intro antiga (Y = startYFactor*charHeight amunt, Z = startZ lluny) → final (0, 0).
  // POSICIÓ, no moviment de càmera. Reversible (segueix el progrés). L'opacitat va a part
  // (showCharacter). Ho crida ScrollItem__WidgetHomeSpacer.onMove amb el progressTop del spacer.
  static setCharacterIntroProgress(p) {
    if (!this.charRig) return;
    const s = CHAR_INTRO_SETTINGS;
    const t = Maths.clamp(p, 0, 1);
    this.charRig.position.y = Maths.lerp(s.startYFactor * this._charHeight(), 0, t);
    this.charRig.position.z = Maths.lerp(s.startZ, 0, t);
  }

  // Fade únic d'opacitat del character (0→1), disparat per la Home a la intro. Es queda a 1
  // (no reverteix). La posició la mou setCharacterIntroProgress via l'scroll.
  static showCharacter() {
    const s = CHAR_INTRO_SETTINGS;
    if (this._charOpacityProxy) gsap.killTweensOf(this._charOpacityProxy);
    this._charOpacityProxy = { o: 0 };
    this._setCharOpacity(0, true);
    gsap.to(this._charOpacityProxy, {
      o: 1, duration: s.duration, ease: 'power2.out',
      onUpdate: () => this._setCharOpacity(this._charOpacityProxy.o, true),
      onComplete: () => { this._setCharOpacity(1); this._charOpacityProxy = null; }
    });
  }

  // La Home crida això quan acaba la intro: activa el parallax del mouse del character.
  static enableCharacterMouse() {
    if (this.character) this.character._mouseInputDisabled = false;
  }


  // ─── Cinemàtica del character en 3 passos (translació pura; vegeu GENERAL_SETTINGS) ───

  // Offset Y del helmet (_scene2) respecte el character, en factor × ALÇADA DEL CHARACTER (món).
  static _setHelmetY(yFactor) {
    if (this.character?._scene2) this.character._scene2.position.y = yFactor * this._charHeight();
  }

  // Alçada DEL CHARACTER en unitats de món: el seu rect DOM (mida CSS en VH). En camera2D, 1px ≈ 1
  // unitat de món a z=0, així que el rect.height (px) ≈ alçada del char en món. És la unitat de tota
  // la cinemàtica Y → mateixa animació a qualsevol pantalla (tracta el breakpoint VH automàticament).
  static _charHeight() {
    const h = this.character?.dom ? this.character.dom.getBoundingClientRect().height : 0;
    return h || Metrics.HEIGHT * 0.7; // fallback ≈ 70vh
  }

  // Factor de pujada del grup al Pas 1 segons breakpoint (× charHeight): mòbil ≤480, tablet ≤1024, desktop.
  static _step1GroupYFactor() {
    const s = GENERAL_SETTINGS;
    if (Metrics.WIDTH <= Sizes.SMARTPHONE) return s.step1GroupYFactorMobile;
    if (Metrics.WIDTH <= Sizes.TABLET_LANDSCAPE) return s.step1GroupYFactorTablet;
    return s.step1GroupYFactor;
  }

  // PAS 1: show helmet snap + puja'l de l'offset de repòs → helmetToYFactor (1s);
  // mou el grup (charRig) en Y (helmet centrat a càmera) i apropa en Z.
  static generalStep1() {
    const s = GENERAL_SETTINGS;
    gsap.killTweensOf(this.charRig.position);

    // Helmet: snap visible + puja de repòs (-20%) → posició sobre el cap
    CharacterAnimationController.showHelmetSnap();
    this._setHelmetY(s.helmetRestYFactor);
    if (this.character?._scene2) {
      gsap.killTweensOf(this.character._scene2.position);
      gsap.to(this.character._scene2.position, {
        y: s.helmetToYFactor * this._charHeight(), duration: s.helmetShowDuration, ease: 'power2.out'
      });
    }

    // Grup global: Y (centra helmet a càmera, × alçada del character, factor per breakpoint) + Z
    gsap.to(this.charRig.position, {
      y: this._step1GroupYFactor() * this._charHeight(), z: s.step1CharZ,
      duration: s.step1Duration, ease: 'power2.inOut'
    });

    // Activa l'animació tapon després d'un delay (respecte l'inici del Pas 1)
    if (this._taponAnimDelayed) this._taponAnimDelayed.kill();
    this._taponAnimDelayed = gsap.delayedCall(s.taponAnimDelay, () => {
      CharacterAnimationController.playTaponAnim();
    });
  }

  // PAS 2: el character torna a la posició inicial (charRig → 0). Y i Z amb durada/ease
  // diferents; la Z comença al step2ZStartFactor de la durada de l'animació tapon.
  static generalStep2(onComplete = null) {
    const s = GENERAL_SETTINGS;
    const taponDur = CharacterAnimationController.getTaponDuration();
    gsap.killTweensOf(this.charRig.position);

    // Y → 0 (ease out)
    gsap.to(this.charRig.position, { y: 0, duration: s.step2YDuration, ease: 'power2.out' });

    // Z → 0 (ease out, diferent), comença al 50% de la durada de l'animació tapon
    gsap.to(this.charRig.position, {
      z: 0, duration: s.step2ZDuration, ease: 'power3.out',
      delay: taponDur * s.step2ZStartFactor,
      onComplete: () => { if (onComplete) onComplete(); }
    });
  }

  // PAS 3: animació acabada → canvi de pàgina (onComplete).
  static generalStep3(onComplete = null) {
    if (onComplete) onComplete();
  }

  // Cinemàtica completa (flux de navegació): pas 1 → (després de step2Delay) pas 2 → pas 3.
  // El changePage es dispara a mig Pas 2 (changePageDelay després d'arrencar-lo): entremig de
  // l'antic (onComplete, final del Pas 2) i el de l'inici del Pas 2 — evita el glitch de transició.
  static animateGeneral(onComplete = null) {
    // Atura el parallax del mouse del character durant la cinemàtica (no s'ha de moure amb el ratolí)
    if (this.character) this.character._mouseInputDisabled = true;
    this.resetGeneral();
    this.generalStep1();
    this._generalSeq = gsap.delayedCall(GENERAL_SETTINGS.step2Delay, () => {
      this.generalStep2(); // Pas 2: el character torna a la posició inicial
      this._changePageDelayed = gsap.delayedCall(GENERAL_SETTINGS.changePageDelay, () => {
        this.generalStep3(onComplete); // changePage a mig camí del Pas 2
      });
    });
  }

  // Reset de la cinemàtica: rig a repòs, helmet ocult al seu offset inicial (-20% Y).
  // NO toca la família del helmet (layer/color): la selecció (setTaponTypesCrossFade) ha de
  // persistir durant l'animació. L'estat inicial luminaris de la Home el posa introCharacter.
  static resetGeneral() {
    this._killGeneralTweens();
    this._resetCharRig(); // mata _generalTL + rig a identitat
    CharacterAnimationController.resetHelmet();
    this._setHelmetY(GENERAL_SETTINGS.helmetRestYFactor); // helmet a l'estat inicial (-20% Y)
  }

  // Mata els tweens/seq de la cinemàtica (helper compartit pels resets snap).
  static _killGeneralTweens() {
    if (this._generalSeq) { this._generalSeq.kill(); this._generalSeq = null; }
    if (this._changePageDelayed) { this._changePageDelayed.kill(); this._changePageDelayed = null; }
    if (this._generalTL) { this._generalTL.kill(); this._generalTL = null; }
    if (this._taponAnimDelayed) { this._taponAnimDelayed.kill(); this._taponAnimDelayed = null; }
    gsap.killTweensOf(this.charRig.position);
    if (this.character?._scene2) gsap.killTweensOf(this.character._scene2.position);
  }

  // Snap del helmet posat: opacity 1 + tapon al frame final + cap amagat (HIDE CABESA 1) + Y sobre el cap.
  static _snapHelmetWorn() {
    CharacterAnimationController.showHelmetSnap();
    CharacterAnimationController.setTaponToEnd();
    const hideName = this.character?.morphTargetNames?.find(n =>
      n.toLowerCase().includes('hide') || n.toLowerCase().includes('cabesa') || n.toLowerCase().includes('cabeza')
    );
    if (hideName) this.character.setMorphInfluence(hideName, 1);
    this._setHelmetY(GENERAL_SETTINGS.helmetToYFactor);
  }

  // Reset del PAS 2 → snap a l'estat FINAL del Pas 1: helmet posat (anim tapon feta) i grup apropat (Y + Z).
  static resetGeneralToStep1End() {
    const s = GENERAL_SETTINGS;
    this._killGeneralTweens();
    this._snapHelmetWorn();
    this.charRig.position.set(0, this._step1GroupYFactor() * this._charHeight(), s.step1CharZ);
  }

  // Reset del PAS 3 → snap a l'estat FINAL del Pas 2: helmet posat i grup tornat a la posició inicial.
  static resetGeneralToStep2End() {
    this._killGeneralTweens();
    this._snapHelmetWorn();
    this.charRig.position.set(0, 0, 0);
  }

  // Mètode públic per controlar l'opacity del tapon (usat per WidgetTapon)
  static setTaponOpacity(value) {
    this._setTaponOpacity(value);
  }

  // Mapeja un tipus (string) al color i preset de llums corresponent
  static _getTypeColor(type) {
    return type === 'luminaris' ? TAPON_SCROLL_SETTINGS.luminarColor : TAPON_SCROLL_SETTINGS.obscurColor;
  }

  // Fixa el color del material del helmet i l'assigna al layer del seu tipus (les llums
  // són persistents per layer, així que no cal lerpar llums). t<0.5 → typeA, sinó typeB.
  static setTaponTypesCrossFade(typeA, typeB, t) {
    CharacterAnimationController.setTaponColors(
      this._getTypeColor(typeA),
      this._getTypeColor(typeB)
    );
    CharacterAnimationController.setTaponCrossFade(t);

    const type = t >= 0.5 ? typeB : typeA;
    if (this.character?._scene2) this.setProductLayer(this.character._scene2, type);
  }


  // Debug: log de l'assignació real de layers (llums + helmet) per verificar l'aïllament
  // per família. layer 0=base, 3=char, 4=luminaris, 5=obscurios. Crida des del debug pane.
  static debugLogLayers() {
    const mask = (o) => o ? o.layers.mask.toString(2).padStart(6, '0') + ` (mask ${o.layers.mask})` : 'n/a';
    console.group('%c[Scene] Layer assignment (bit0=L0 … bit5=L5)', 'font-weight:bold');
    console.log('LLUMS:');
    console.log('  ambient        :', mask(this._ambientLight));
    console.log('  LUM  dir1/2/pt :', mask(this._prodDir1), mask(this._prodDir2), mask(this._prodPoint));
    console.log('  OBS  dir1/2/pt :', mask(this._obsDir1), mask(this._obsDir2), mask(this._obsPoint));
    console.log('  CHAR dir1/2/pt :', mask(this._charDir1), mask(this._charDir2), mask(this._charPoint));
    if (this.character?._scene2) {
      console.log('HELMET (_scene2) meshes:');
      this.character._scene2.traverse(o => { if (o.isMesh) console.log(`  ${o.name || 'mesh'} :`, mask(o)); });
    }
    console.log('Esperat → LUM a L4 (mask 16), OBS a L5 (mask 32), ambient a {0,4,5} (mask 49), __obj a L2 (mask 4)');
    console.groupEnd();
  }

  // Reset de les llums del tapon.glb (helmet): torna al layer/tipus per defecte (luminaris).
  static resetTaponLights() {
    if (this.character?._scene2) this.setProductLayer(this.character._scene2, 'luminaris');
  }

  // Assigna el layer d'il·luminació segons el tipus: luminaris → PROD_LAYER (4),
  // obscurios → OBSCURIOS_LAYER (5). layers.set() treu layer 0 → Pass A no els renderitza.
  // type/categ === 'luminaris' → PROD_LAYER (4); qualsevol altre valor → OBSCURIOS_LAYER (5)
  static setProductLayer(object, type = 'luminaris') {
    if (type !== 'luminaris' && type !== 'obscurios') {
      console.warn(`[Scene] setProductLayer: categ inesperat "${type}" → cau a obscurios (L5). Revisa el data-categ/data-type de l'objecte.`, object);
    }
    const layer = type === 'luminaris' ? PROD_LAYER : OBSCURIOS_LAYER;
    object.traverse(obj => {
      if (obj.isMesh) obj.layers.set(layer);
    });
  }

  // ─── Character state (HOME / PRODUCTS) ───────────────────────────────────

  // Canvia l'state: simplement reassigna character.dom al nou target.
  // Scene.loop() lerpa posició i mida automàticament cada frame.
  static setCharacterState(state, targetEl = null) {
    this.characterState = state;
    const el = targetEl || this._targetEls[state];
    if (!el || !this.character) return;
    this._targetEls[state] = el;
    this.character.dom = el;
  }

  // Inicialitza character+tapon per a la pàgina Products.
  // - Ve de Home (characterVisible=true): cancel·la hide, ajusta estat sense tocar opacity.
  // - Ve de pàgina sense character (characterVisible=false): setup complet amb snap a opacity 1.
  static initCharacterForProducts(container) {
    if (!this.character) return;
    if (this._productsReady) return; // ja inicialitzat per Products → no repetim el setup

    // Si el model encara s'està carregant (p.ex. entrada directa a Products), ajornem el setup
    // fins al .then() de _initCharacter. Si no, el .then() faria _setCharOpacity(0) DESPRÉS
    // d'aquest setup i el character quedaria invisible (el bug "alguns cops" no es veu).
    if (!this._charReady) {
      this._pendingProductsContainer = container;
      return;
    }

    // Cancel·la qualsevol fade de hideCharacter en curs
    if (this._hideProxy) {
      gsap.killTweensOf(this._hideProxy);
      this._hideProxy = null;
    }

    // Rig de la pose cinemàtica → identitat, suavitzat (en comptes de snap)
    this._animateCharRigToRest();

    // 1. Target del character a la pàgina Products → el loop lerpará cap a ell
    const targetEl = GetBy.class('__character-target', container)[0];
    if (targetEl) this.setCharacterState('PRODUCTS', targetEl);

    // Rotació base de Products (-30°): NO la snapem. El character arriba mirant al centre
    // (_charBaseRotY = 0) i animem la base a -30° una mica més tard —un punt intermig entre
    // el show de la pàgina i el dels thumbnails— perquè la rotació entretingui l'usuari.
    // El loop hi lerpa suaument. Retard tunejable: CHAR_ROT_SETTINGS.productsBaseDelay.
    this._charBaseRotY = 0;
    this.character.rot.y = 0; // arriba mirant al centre (determinista, encara que vingui d'un -30° previ)
    if (this._charRotDelayed) this._charRotDelayed.kill();
    this._charRotDelayed = gsap.delayedCall(CHAR_ROT_SETTINGS.productsBaseDelay, () => {
      this._charBaseRotY = Maths.toRadians(CHAR_ROT_SETTINGS.productsBaseDeg);
    });

    // 2. Llums i color del tapon segons data-type del container
    const dataType = container.dataset.type || 'luminaris';
    this.setTaponTypesCrossFade(dataType, dataType, 0);
    this.initProductLights(dataType);

    // 3. Visibilitat: sempre a l'estat correcte de Products
    this._charScrollZ = 0;
    this._setCharOpacity(1);
    this._setTaponOpacity(1);
    this._setHelmetY(GENERAL_SETTINGS.helmetToYFactor); // helmet sobre el cap (no a l'offset de repòs)

    // 4. HIDE CABESA = 1
    const hideName = this.character.morphTargetNames?.find(n =>
      n.toLowerCase().includes('hide') || n.toLowerCase().includes('cabesa') || n.toLowerCase().includes('cabeza')
    );
    if (hideName) this.character.setMorphInfluence(hideName, 1);

    // 5. Atura _BASE (Products: character estàtic) + _TAPON al frame final
    CharacterAnimationController.stopBase();
    CharacterAnimationController.setTaponToEnd();

    // Sense repulsió del mouse a Products (es reactiva a Home); reset del grup a identitat per si
    // venia inclinat/desplaçat de la darrera interacció a Home.
    this.character._hasRepulsion = false;
    this.character.repulsionGroup.rotation.set(0, 0, 0);
    this.character.repulsionGroup.position.set(0, 0, 0);

    this.characterVisible = true;
    this._productsReady = true;
  }

  // Velocitat de scroll de ProductsScene → rotació Y del character+tapon (com bottleGroup)
  static setScrollVelocity(vel) {
    this._scrollVel = vel;
  }

  // Torna al target HOME
  static resetCharacterToHome() {
    this._scrollVel = 0; // atura la rotació per scroll
    this._productsReady = false; // surt de l'estat Products
    this._pendingProductsContainer = null; // cancel·la un setup de Products ajornat (si n'hi havia)
    if (this._charRotDelayed) { this._charRotDelayed.kill(); this._charRotDelayed = null; }
    this._charBaseRotY = 0; // treu la rotació base de Products
    this.setCharacterState('HOME', this._targetEls['HOME'] || GetBy.id('characterTarget'));
  }

  // ─── Product lights API ───────────────────────────────────────────────────
  // Les llums de producte ara són PERSISTENTS per layer (vegeu _initProductLights):
  // luminaris (L4) i obscurios (L5) sempre presents. Cada objecte va al seu layer amb
  // Scene.setProductLayer(obj, type). Aquests mètodes es mantenen com a no-op per
  // compatibilitat amb els callers existents (ProductsScene / ProductScene / ArtScene).
  static initProductLights() { /* no-op: llums persistents per layer */ }
  static disposeProductLights() { /* no-op: llums persistents per layer */ }

  // __obj va al seu propi layer (OBJ_LAYER=2) → render pass propi (l'últim), SENSE llums.
  // layers.set() el treu de layer 0 perquè no es renderitzi al Pass 1 (character + thumbnails).
  static setObjLayer(object) {
    object.traverse(obj => {
      if (obj.isMesh) obj.layers.set(OBJ_LAYER);
    });
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  static start() {
    if (this._initialized) return;
    this._initialized = true;
  }

  static loop() {
    BGSphere.loop();


    // Rotació Y del character basada en velocitat de scroll (Products page — com bottleGroup)
    // El signe de CHAR_ROT_SETTINGS.speed marca el sentit (negatiu = contrari als thumbnails)
    if (this.character?.active) {
      // Rotació base (Products: -30°; Home: 0) + rotació per velocitat de scroll
      this.character.rot.y = Maths.lerp(
        this.character.rot.y,
        this._charBaseRotY + this._scrollVel * CHAR_ROT_SETTINGS.speed,
        0.05
      );
    }

    if (this.character?.active) {
      let { x, y, width, height } = this.character.dom.getBoundingClientRect();

      if (width === 0 || height === 0) {
        width = Metrics.WIDTH * 0.35;
        height = Metrics.WIDTH * 0.35;
        x = (Metrics.WIDTH - width) * 0.5;
        y = (Metrics.HEIGHT - height) * 0.5;
      }

      // Posició i mida objectiu (llegides del dom actual, que canvia amb l'state)
      const tPos = this.character.domPositionTo3D(x, y);
      const tPosX = tPos.x + width * 0.5;
      const tPosY = tPos.y - height * 0.5;
      const tScale = this.character.sizeFactor ? width / this.character.sizeFactor : width / 20;

      // Lerp de posició i mida — suavitza el canvi d'state (HOME↔PRODUCTS)
      const LF = 0.08;
      this.character.pos.x = Maths.lerp(this.character.pos.x, tPosX, LF);
      this.character.pos.y = Maths.lerp(this.character.pos.y, tPosY, LF);
      this.character.pos.z = this.character.dataZ + this._charScrollZ;

      const cScale = this.character.object?.scene.scale.x ?? tScale;
      const lScale = Maths.lerp(cScale, tScale, LF);
      this.character.object?.scene.scale.setScalar(lScale);
      if (this.character._scene2) this.character._scene2.scale.setScalar(lScale);

      this.character.update();
    }
  }

  static resize() {
    BGSphere.resize(GetBy.id('scene-three'));
    if (this.character) this.character.resize();

    const cs = CHAR_LIGHTS;
    if (this._charDir1) this._charDir1.position.set(cs.dir1.posX * Metrics.WIDTH, cs.dir1.posY * Metrics.HEIGHT, cs.dir1.posZ * Metrics.WIDTH);
    if (this._charDir2) this._charDir2.position.set(cs.dir2.posX * Metrics.WIDTH, cs.dir2.posY * Metrics.HEIGHT, cs.dir2.posZ * Metrics.WIDTH);
    if (this._charPoint) {
      this._charPoint.position.set(cs.point.posX * Metrics.WIDTH, cs.point.posY * Metrics.HEIGHT, cs.point.posZ * Metrics.WIDTH);
      this._charPoint.intensity = cs.point.intensity * Metrics.WIDTH;
    }

    if (this._prodDir1) {
      // Posició + intensitat segons el mode actual (home frontal / default lateral);
      // el lookAt de família el re-alimenta HomeScene.loop cada frame.
      this._applyProductLightPositions(this._lightsMode === 'home');
    }
  }

  // ─── BGSphere visibility (mateix tractament que el character) ──────────────
  // Show idèntic per a Home i Products. Hide a Default.beforeShow.
  // _bgVisible evita re-fer show/hide si ja s'està en aquell estat. El fade in/out el
  // porta el propi shader del BGSphere (uKill via revealRects/killRects) — abans hi havia
  // un dissolve via TransitionController (render target + quad, doble render cada frame).

  // Compila el shader del BGSphere si encara no ho està, SENSE fer-lo visible. Cridar-ho
  // com a "warm-up" amb temps de marge (p.ex. a l'inici de la transició d'entrada a Home,
  // no just abans de l'intro) evita que showBGSphere() faci la compilació al moment crític.
  static warmBGSphere() {
    if (!BGSphere._sphere) BGSphere.init(GetBy.id('scene-three'));
  }

  static showBGSphere() {
    if (this._bgVisible) return;
    this._bgVisible = true;
    this.warmBGSphere(); // no-op si ja s'ha escalfat abans
    this.revealRects();
  }

  static hideBGSphere() {
    if (!this._bgVisible) return;
    this._bgVisible = false;
    this.killRects();
  }

  // ─── Misc (backwards compat) ──────────────────────────────────────────────

  static add(element) { this.group.add(element); }
  static remove(element) { this.group.remove(element); }
  static revealRects(duration) { BGSphere.revealRects(duration); }
  static killRects(duration) { BGSphere.killRects(duration); }
  static reseed(cb) { BGSphere.reseed(cb); }
  static changePalette(bgCss, rectCss, duration) { BGSphere.changePalette(bgCss, rectCss, duration); }

  static dispose() {
    BGSphere.dispose();
    WebGLSketch.group.remove(this.group);
    WebGLSketch.scene.remove(this.charGroup);
  }
}
